/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import SearchUtils from '../../../utils/SearchUtils';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import { APP_TYPES, SEARCH_CCD_CCH_SAMCAP_CGEN } from '../../../containers/SearchResultsPage/SearchConstants';
import Carousel from '@cchome/carousel';
import SearchDropdownSparkCard from './SearchDropdownSparkCard/SearchDropdownSparkCard';
import './SearchDropdownSparkTemplates.scss';
import HorizontalRuler from '../../HorizontalRuler';

type OwnProps = {
	resultItems: Array<ResultItem>;
	titleKey: string;
	logger: Logger;
	searchHostContext: HostContext;
	searchQuery: string;
	viewAllLocation: string;
	showRuler?: boolean;
	appId: string;
	cgenMap: Map<string, Record<string, any>>;
	viewAllUrl: string;
	analyticCallbacks: {
		render: (data, analytics) => unknown;
		templateClick: (data, analytics, index) => unknown;
		carouselScroll: (e, analytics) => unknown;
		viewAll: (analytics) => unknown;
	};
	showName?: boolean;
	showHeaderIcon?: boolean;
};

type SearchDropDownSparkTemplatesProps = OwnProps & PropsFromRedux;

class SearchDropdownSparkTemplates extends ClickBase<SearchDropDownSparkTemplatesProps> {
	transformTimeout;
	templateDivContainer: HTMLDivElement;

	componentDidMount(): void {
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		this.props.analyticCallbacks.render(this.props.resultItems, analytics);
	}
	render(): ReactNode {
		if (!this.props.resultItems || this.props.resultItems.length === 0 || !this.props.searchAppMetadataList) {
			return null;
		}

		return (
			<>
				<div className='search-dropdown-spark-templates-container'>
					<div className='search-dropdown-spark-templates-header text-container'>
						{this.renderTitle()}
						{this.renderViewAll()}
					</div>
					<div className='search-dropdown-spark-templates' ref={(e) => (this.templateDivContainer = e)}>
						<Carousel
							isCCX={SearchUtils.applyBorderBox()}
							darkMode={SearchUtils.isDarkMode(this.props.theme)}
							cardType='SEARCH_UPDATED'
							actionEvents={this.onCarouselClick}
							keepWidth
						>
							{this.renderSparkTemplatesResults(this.props.resultItems)}
						</Carousel>
					</div>
				</div>
				{this.props.showRuler && <HorizontalRuler />}
			</>
		);
	}

	/**
	 * [renderCCExpressResults renders the CCExpress results]
	 * @returns {[array]}
	 */
	renderSparkTemplatesResults(resultItems: Array<ResultItem>) {
		// Collect Search Feedback Count Data
		SearchUtils.setSearchResultsCountToSessionStorage('popular_spark_templates', resultItems);
		return resultItems.slice(0, SearchAnalytics.sliceValue).map((resultItem, index) => (
			<div key={index} className={`item-${index}`}>
				<a target='_blank' tabIndex={-1}>
					<SearchDropdownSparkCard
						onTempalteClick={this.onClick.bind(this, resultItem, index)}
						showName={this.props.showName}
						resultItem={resultItem}
						isDarkMode={SearchUtils.isDarkMode(this.props.theme)}
						index={index}
						lastCard={index === resultItems.length - 1}
					/>
				</a>
			</div>
		));
	}

	/**
	 * Post results of selection
	 * @param {ResultItem} resultItem
	 * @param {selected card} index
	 */
	onClick(resultItem: ResultItem, index: number) {
		const url = resultItem.attributes.openUrl || '';
		AnalyticsUtils.location = `Search:template:${this.props.viewAllLocation}`;

		AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
		let appendedUrl = AnalyticsUtils.appendUrlWithAnalytics(url);
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		this.props.analyticCallbacks.templateClick(resultItem, analytics, index);
		const resultItemCopy = JSON.parse(JSON.stringify(resultItem));
		const {
			hostInfo: { surface, client },
		} = this.searchHostContext;
		let cgenQueryParam = this.props.cgenMap?.get(surface)?.click;
		if (surface !== SEARCH_CCD_CCH_SAMCAP_CGEN.ccdHost) {
			cgenQueryParam = this.props.cgenMap?.get(client)?.click;
		}
		let queryParamExists = appendedUrl.includes('?');
		if (surface === SEARCH_CCD_CCH_SAMCAP_CGEN.ccdHost) {
			resultItemCopy.attributes.openUrl = queryParamExists
				? appendedUrl.concat('&', SEARCH_CCD_CCH_SAMCAP_CGEN.samcapId)
				: appendedUrl.concat('?', SEARCH_CCD_CCH_SAMCAP_CGEN.samcapId);

			appendedUrl = resultItemCopy.attributes.openUrl;
			queryParamExists = true;
		}
		if (cgenQueryParam) {
			resultItemCopy.attributes.openUrl = queryParamExists
				? appendedUrl.concat('&', cgenQueryParam)
				: appendedUrl.concat('?', cgenQueryParam);
		} else {
			resultItemCopy.attributes.openUrl = appendedUrl;
		}
		const sparkResultItem: ClickResult = {
			action: 'open',
			item: resultItemCopy,
		};
		this.onClickCard(this.props.searchHostContext, APP_TYPES.SPARK_TEMPLATES, sparkResultItem);
	}

	/**
	 * Post the user clicks on the Carousel
	 */
	onCarouselClick = (e: any) => {
		clearTimeout(this.transformTimeout);
		this.templateDivContainer?.classList?.add?.('transforming');
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		this.props.analyticCallbacks.carouselScroll(e, analytics);
		this.transformTimeout = setTimeout(() => {
			this.templateDivContainer?.classList?.remove?.('transforming');
		}, 500);
	};

	renderViewAll() {
		const translationId = 'app.search.see_more';
		return (
			<span
				className='see-all-text'
				onKeyDown={(event) => SearchUtils.onKeyDown(event)}
				onClick={this.onClickSeeAll.bind(this)}
				tabIndex={0}
				role='link'
			>
				<a role='navigation' className='link-out-tag'>
					<span className='all-text'>
						<FormattedMessage id={translationId} />
					</span>
				</a>
			</span>
		);
	}

	onClickSeeAll() {
		let { viewAllUrl } = this.props;
		if (!viewAllUrl) {
			this.props.logger.error('CCExpress Templates', 'Incorrect CCExpress Templates view all link', viewAllUrl);
		}

		AnalyticsUtils.location = `Search:view-all:${this.props.viewAllLocation}`;
		AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
		viewAllUrl = AnalyticsUtils.appendUrlWithAnalytics(viewAllUrl);
		const analytics = new SearchAnalytics(this.props.searchHostContext);
		this.props.analyticCallbacks.viewAll(analytics);
		this.onClickShowAllBase(this.props.searchHostContext, APP_TYPES.SPARK_TEMPLATES, {
			openUrl: viewAllUrl,
		});
	}

	renderTitle() {
		const appMetadata = this.props.searchAppMetadataList;
		const icons = appMetadata.apps[this.props.appId]?.iconUrls;
		let iconUrl;
		if (icons) {
			iconUrl = SearchUtils.getAppIconUrl(icons);
		}
		return (
			<div className='search-dropdown-spark-templates-title-container'>
				{iconUrl && this.props.showHeaderIcon && <img alt='' className='spark-logo' src={iconUrl} />}
				<span className='search-dropdown-spark-templates-title'>
					<h5>
						<FormattedMessage id={this.props.titleKey} />
					</h5>
				</span>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchConfig: search.searchConfig,
	searchAppMetadataList: search.searchAppMetadataList,
	searchQueryValue: search.searchQuery,
	theme: search.theme,
});

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SearchDropdownSparkTemplates);
